// Home: the bento grid shown when no query is open. Tiles reuse the look's .card / .card-head / .card-title,
// so every look styles them; the grid itself and the tile insides are in each look's "bento" block.
import { parseTable } from "../sample";

type Query = { id: number; title: string; notes: string; sql: string; tags: string; pinned: number; created_at: string; updated_at: string; samples?: number };
type RecentSample = { id: number; query_id: number; name: string; type: string; size: number; created_at: string; title: string };
type Hooks = { open: (id: number) => void; newQuery: () => void; filterTag: (t: string) => void; copy: (q: Query) => void };

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const icon = (name: string) => `<svg class="i" aria-hidden="true"><use href="#i-${name}"/></svg>`;
const dateOf = (s: string) => new Date(`${s.replace(" ", "T")}Z`);
const tagsOf = (q: Query) => q.tags.split(",").map((t) => t.trim()).filter(Boolean);
function ago(s: string) {
  const sec = (Date.now() - dateOf(s).getTime()) / 1000;
  if (sec < 60) return "now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d ago`;
  return dateOf(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const tile = (cls: string, title: string, sub: string, body: string) =>
  `<section class="card tile ${cls}"><header class="card-head"><span class="card-title">${title}</span><span class="card-sub">${sub}</span></header><div class="tile-body">${body}</div></section>`;
const row = (q: Query, right: string) =>
  `<li><button class="tile-row" data-open="${q.id}"><span class="tr-title">${esc(q.title)}</span><span class="tr-meta">${right}</span></button></li>`;

let samples: RecentSample[] = [];
let samplesFetched = 0;
const previews = new Map<number, string>();

export function renderHome(queries: Query[], hooks: Hooks) {
  const home = document.getElementById("home")!;
  const welcome = document.getElementById("welcome")!;
  const empty = document.getElementById("empty")!;
  const has = queries.length > 0;
  home.hidden = !has;
  welcome.hidden = has;
  empty.classList.toggle("has-home", has);
  if (!has) return;

  const counts = new Map<string, number>();
  for (const t of queries.flatMap(tagsOf)) counts.set(t, (counts.get(t) ?? 0) + 1);
  const tags = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const max = Math.max(1, ...counts.values());
  const week = Date.now() - 7 * 86400_000;
  const addedThisWeek = queries.filter((q) => dateOf(q.created_at).getTime() > week).length;
  const sampleTotal = queries.reduce((n, q) => n + (q.samples ?? 0), 0);
  const pinned = queries.filter((q) => q.pinned);
  const recent = [...queries].sort((a, b) => dateOf(b.updated_at).getTime() - dateOf(a.updated_at).getTime()).slice(0, 6);
  const gaps = queries
    .map((q) => ({ q, missing: [!q.notes.trim() && "notes", !q.samples && "sample", !tagsOf(q).length && "tags"].filter(Boolean) as string[] }))
    .filter((x) => x.missing.length)
    .sort((a, b) => b.missing.length - a.missing.length);

  const stat = (n: number, label: string) => `<div class="stat"><span class="stat-n">${n}</span><span class="stat-l">${label}</span></div>`;
  home.innerHTML = [
    tile("t-hero", "Overview", "", `
      <div class="stats">${stat(queries.length, "queries")}${stat(sampleTotal, "samples")}${stat(counts.size, "tags")}${stat(addedThisWeek, "new this week")}</div>
      <div class="hero-actions"><button class="btn primary" data-new>${icon("plus")}New query</button>
      <span class="hint">Pick a query on the left, or press <kbd>/</kbd> to search.</span></div>`),

    tile(pinned.length > 2 ? "t-pinned tall" : "t-pinned", "Pinned", pinned.length ? String(pinned.length) : "", pinned.length
      ? `<ul class="tile-list">${pinned.map((q) => `<li class="pin-row">
          <button class="tile-row" data-open="${q.id}"><span class="tr-title">${esc(q.title)}</span><span class="tr-meta">${esc(tagsOf(q).map((t) => `#${t}`).join(" "))}</span></button>
          <button class="btn ghost small" data-copy="${q.id}" title="Copy SQL">${icon("copy")}Copy</button></li>`).join("")}</ul>`
      : `<p class="tile-empty">Star a query (${icon("star")} at the top of a query) to pin it here, with a one-click Copy.</p>`),

    tile("t-recent", "Recently edited", "", `<ul class="tile-list">${recent.map((q) => row(q, ago(q.updated_at))).join("")}</ul>`),

    tile("t-tags", "Tags", String(counts.size), tags.length
      ? `<div class="cloud">${tags.map(([t, n]) => `<button class="cloud-tag" data-tag="${esc(t)}" style="--w:${(n / max).toFixed(2)}" title="${n} quer${n === 1 ? "y" : "ies"}">${esc(t)}<span class="n">${n}</span></button>`).join("")}</div>`
      : `<p class="tile-empty">No tags yet. Add some on a query (comma separated).</p>`),

    tile("t-samples", "Latest samples", "", `<div id="homeSamples" class="sample-cards"><p class="tile-empty">Loading…</p></div>`),

    tile("t-gaps", "Needs attention", gaps.length ? String(gaps.length) : "", gaps.length
      ? `<ul class="tile-list">${gaps.slice(0, 6).map(({ q, missing }) => row(q, missing.map((m) => `<span class="badge">no ${m}</span>`).join(""))).join("")}</ul>`
      : `<p class="tile-empty">Every query has notes, tags and a sample. Tidy!</p>`),

    tile("t-keys", "Shortcuts", "", `<dl class="keys">
      <div><dt><kbd>/</kbd></dt><dd>Search</dd></div>
      <div><dt><kbd>E</kbd></dt><dd>Edit / done</dd></div>
      <div><dt><kbd>⌘</kbd><kbd>⇧</kbd><kbd>C</kbd></dt><dd>Copy SQL</dd></div>
      <div><dt><kbd>⇧</kbd><kbd>⌥</kbd><kbd>F</kbd></dt><dd>Format SQL</dd></div>
      <div><dt><kbd>⌘</kbd><kbd>S</kbd></dt><dd>Save now</dd></div>
      <div><dt><kbd>⌘</kbd><kbd>V</kbd></dt><dd>Paste a result as a sample</dd></div></dl>`),
  ].join("");

  home.onclick = (e) => {
    const t = e.target as HTMLElement;
    const open = t.closest<HTMLElement>("[data-open]");
    if (open) return hooks.open(Number(open.dataset.open));
    const copy = t.closest<HTMLElement>("[data-copy]");
    if (copy) { const q = queries.find((x) => x.id === Number(copy.dataset.copy)); if (q) hooks.copy(q); return; }
    const tag = t.closest<HTMLElement>("[data-tag]");
    if (tag) return hooks.filterTag(tag.dataset.tag!);
    if (t.closest("[data-new]")) hooks.newQuery();
  };

  // samples change rarely: fetch at most every 30 s, and draw from the cache meanwhile
  if (Date.now() - samplesFetched > 30_000) {
    samplesFetched = Date.now();
    fetch("/api/samples").then((r) => (r.ok ? r.json() : [])).then((s: RecentSample[]) => { samples = s; drawSamples(); }).catch(() => {});
  }
  drawSamples();
}

function drawSamples() {
  const box = document.getElementById("homeSamples");
  if (!box) return;
  if (!samples.length) {
    box.innerHTML = `<p class="tile-empty">No sample output yet. Open a query and paste (${"⌘V"}) or drop what it returned.</p>`;
    return;
  }
  box.innerHTML = samples.map((s) => `<button class="sample-card" data-open="${s.query_id}" title="${esc(s.name)}">
      <span class="sc-preview" data-preview="${s.id}">${previews.get(s.id) ?? ""}</span>
      <span class="sc-title">${esc(s.title)}</span><span class="sc-meta">${esc(s.name)} · ${ago(s.created_at)}</span></button>`).join("");
  for (const s of samples) if (!previews.has(s.id)) loadPreview(s);
}

/** A tiny look at the sample: a thumbnail, the first rows of a table, or the first lines of text. */
async function loadPreview(s: RecentSample) {
  let html: string;
  if (s.type.startsWith("image/")) {
    html = `<img src="/api/samples/${s.id}" alt="" loading="lazy" />`;
  } else {
    const text = await fetch(`/api/samples/${s.id}`).then((r) => (r.ok ? r.text() : "")).catch(() => "");
    const t = parseTable(text, s.name);
    html = t
      ? `<table class="mini"><tr>${t.columns.slice(0, 4).map((c) => `<th>${esc(c)}</th>`).join("")}</tr>${t.rows.slice(0, 3)
          .map((r) => `<tr>${r.slice(0, 4).map((v) => `<td>${esc(v ?? "NULL")}</td>`).join("")}</tr>`).join("")}</table>`
      : `<pre>${esc(text.split("\n").slice(0, 4).join("\n"))}</pre>`;
  }
  previews.set(s.id, html);
  const slot = document.querySelector(`[data-preview="${s.id}"]`);
  if (slot) slot.innerHTML = html;
}
